import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { 
  ChefHat, Star, MapPin, Calendar, Users, Clock, Search, Filter,
  Heart, DollarSign, Award, Check, MessageCircle, ArrowRight,
  Sparkles, Crown, Globe, Briefcase, Languages, Phone, Mail,
  Plus, X, Upload, CheckCircle, AlertCircle, Flame, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const SERVICE_TYPES = [
  { value: 'romantic_dinner', labelFr: 'Dîner Romantique', labelEn: 'Romantic Dinner', emoji: '💕' },
  { value: 'family_party', labelFr: 'Fête de Famille', labelEn: 'Family Party', emoji: '👨‍👩‍👧‍👦' },
  { value: 'corporate_event', labelFr: 'Événement Corporate', labelEn: 'Corporate Event', emoji: '🏢' },
  { value: 'cooking_class', labelFr: 'Cours de Cuisine', labelEn: 'Cooking Class', emoji: '👨‍🍳' },
  { value: 'wedding', labelFr: 'Mariage', labelEn: 'Wedding', emoji: '💒' },
  { value: 'birthday', labelFr: 'Anniversaire', labelEn: 'Birthday', emoji: '🎂' },
  { value: 'catering', labelFr: 'Traiteur', labelEn: 'Catering', emoji: '🍽️' },
];

const REGIONS = [
  { value: 'tunis', label: 'Tunis' },
  { value: 'sfax', label: 'Sfax' },
  { value: 'sousse', label: 'Sousse' },
  { value: 'kairouan', label: 'Kairouan' },
  { value: 'bizerte', label: 'Bizerte' },
  { value: 'gabes', label: 'Gabès' },
  { value: 'medenine', label: 'Médenine' },
  { value: 'nabeul', label: 'Nabeul' },
  { value: 'tozeur', label: 'Tozeur' },
  { value: 'kef', label: 'Le Kef' },
];

const REQUIRED_LEVEL = 'Chef de Cuisine'; // Niveau requis pour devenir chef privé
const REQUIRED_BARKA = 100; // Points BARKA requis

// Chef Card Component
const ChefCard = ({ chef, onBook }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
    >
      <Card className="overflow-hidden rounded-2xl group cursor-pointer hover:shadow-xl transition-all">
        <div className="relative">
          {/* Header Image */}
          <div className="h-32 bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] relative">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
            {/* Verified Badge */}
            {chef.user?.is_verified && (
              <Badge className="absolute top-3 right-3 bg-green-500 text-white">
                <CheckCircle className="w-3 h-3 mr-1" />
                Vérifié
              </Badge>
            )}
            {/* Experience Badge */}
            <Badge className="absolute top-3 left-3 bg-white/20 text-white backdrop-blur">
              {chef.years_experience} ans exp.
            </Badge>
          </div>
          
          {/* Avatar */}
          <Avatar className="w-24 h-24 absolute left-1/2 -translate-x-1/2 -bottom-12 border-4 border-white shadow-lg">
            <AvatarImage src={chef.user?.profile_picture} />
            <AvatarFallback className="bg-[#E07A5F] text-white text-2xl">
              {chef.user?.display_name?.[0] || chef.user?.username?.[0] || 'C'}
            </AvatarFallback>
          </Avatar>
        </div>

        <CardContent className="pt-16 pb-6 px-6 text-center">
          {/* Name & Tagline */}
          <h3 className="font-fraunces text-xl font-bold">
            {chef.user?.display_name || chef.user?.username}
          </h3>
          <p className="text-gray-500 text-sm mt-1 line-clamp-1">{chef.tagline}</p>
          
          {/* Rating */}
          <div className="flex items-center justify-center gap-2 mt-3">
            <div className="flex items-center">
              <Star className="w-5 h-5 text-[#FFD700] fill-[#FFD700]" />
              <span className="font-bold ml-1">{chef.rating?.toFixed(1) || '5.0'}</span>
            </div>
            <span className="text-gray-400">•</span>
            <span className="text-gray-500 text-sm">{chef.reviews_count || 0} avis</span>
          </div>
          
          {/* Location */}
          <div className="flex items-center justify-center gap-1 text-gray-500 text-sm mt-2">
            <MapPin className="w-4 h-4" />
            {chef.city}, {REGIONS.find(r => r.value === chef.region)?.label || chef.region}
          </div>
          
          {/* Services */}
          <div className="flex flex-wrap justify-center gap-1 mt-4">
            {chef.services?.slice(0, 3).map((service) => {
              const serviceInfo = SERVICE_TYPES.find(s => s.value === service);
              return (
                <Badge 
                  key={service} 
                  variant="outline"
                  className="text-xs"
                >
                  {serviceInfo?.emoji} {serviceInfo?.labelFr}
                </Badge>
              );
            })}
            {chef.services?.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{chef.services.length - 3}
              </Badge>
            )}
          </div>
          
          {/* Price & CTA */}
          <div className="mt-6 pt-4 border-t">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <p className="text-xs text-gray-500">À partir de</p>
                <p className="font-bold text-[#1E4ED8] text-lg">
                  {chef.base_price_per_person} TND<span className="text-xs text-gray-500">/pers</span>
                </p>
              </div>
              <Button 
                className="bg-[#E07A5F] hover:bg-[#D9381E] rounded-full"
                onClick={() => onBook(chef)}
              >
                Réserver
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Become Chef Dialog
const BecomeChefDialog = ({ open, onClose, user, onSubmit }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    tagline: '',
    bio: '',
    years_experience: 1,
    training: '',
    specialties: [],
    signature_dishes: [],
    region: '',
    city: '',
    services: [],
    base_price_per_person: 40,
    languages: ['Français', 'Arabe'],
  });
  const [newDish, setNewDish] = useState('');
  const [newSpecialty, setNewSpecialty] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const userBarka = user?.barka_points || user?.barka || 0;
  const isEligible = userBarka >= REQUIRED_BARKA;

  const handleAddDish = () => {
    if (newDish.trim() && formData.signature_dishes.length < 5) {
      setFormData({ ...formData, signature_dishes: [...formData.signature_dishes, newDish.trim()] });
      setNewDish('');
    }
  };

  const handleAddSpecialty = () => {
    if (newSpecialty.trim() && formData.specialties.length < 5) {
      setFormData({ ...formData, specialties: [...formData.specialties, newSpecialty.trim()] });
      setNewSpecialty('');
    }
  };

  const handleSubmit = async () => {
    if (!isEligible) {
      toast.error(`Tu dois avoir au moins ${REQUIRED_BARKA} BARKA pour devenir chef privé`);
      return;
    }

    if (!formData.tagline || !formData.bio || !formData.region || !formData.city || formData.services.length === 0) {
      toast.error('Remplis tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
      toast.success('Profil chef privé créé avec succès!');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du profil');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-fraunces text-2xl flex items-center gap-2">
            <ChefHat className="w-6 h-6 text-[#E07A5F]" />
            Devenir Chef Privé
          </DialogTitle>
          <DialogDescription>
            Propose tes services culinaires et gagne de l'argent en partageant ta passion
          </DialogDescription>
        </DialogHeader>

        {/* Eligibility Check */}
        {!isEligible ? (
          <div className="p-6 text-center">
            <div className="w-20 h-20 rounded-full bg-[#E07A5F]/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-10 h-10 text-[#E07A5F]" />
            </div>
            <h3 className="font-fraunces text-xl font-bold mb-2">Niveau insuffisant</h3>
            <p className="text-gray-500 mb-4">
              Tu dois avoir au moins <strong>{REQUIRED_BARKA} BARKA</strong> pour devenir chef privé.
            </p>
            <div className="bg-gray-100 rounded-full p-4 max-w-xs mx-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">Ta progression</span>
                <span className="font-bold text-[#E07A5F]">{userBarka} / {REQUIRED_BARKA}</span>
              </div>
              <Progress value={(userBarka / REQUIRED_BARKA) * 100} className="h-3" />
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Continue à partager des recettes et participer aux défis pour gagner des BARKA!
            </p>
          </div>
        ) : (
          <>
            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 my-4">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    step >= s ? 'bg-[#1E4ED8] text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {s}
                  </div>
                  {s < 3 && <div className={`w-12 h-1 ${step > s ? 'bg-[#1E4ED8]' : 'bg-gray-200'}`} />}
                </React.Fragment>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {/* Step 1: Basic Info */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div>
                    <Label>Ton slogan *</Label>
                    <Input
                      placeholder="Ex: La cuisine tunisienne comme à la maison de ma grand-mère"
                      value={formData.tagline}
                      onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <Label>Présentation *</Label>
                    <Textarea
                      placeholder="Décris ton parcours, ta passion et ce qui te rend unique..."
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Années d'expérience</Label>
                      <Input
                        type="number"
                        min={1}
                        max={50}
                        value={formData.years_experience}
                        onChange={(e) => setFormData({ ...formData, years_experience: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Formation</Label>
                      <Input
                        placeholder="Ex: École Hôtelière de Tunis"
                        value={formData.training}
                        onChange={(e) => setFormData({ ...formData, training: e.target.value })}
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Services & Location */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div>
                    <Label>Services proposés *</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {SERVICE_TYPES.map((service) => (
                        <label
                          key={service.value}
                          className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                            formData.services.includes(service.value)
                              ? 'border-[#1E4ED8] bg-[#1E4ED8]/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Checkbox
                            checked={formData.services.includes(service.value)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({ ...formData, services: [...formData.services, service.value] });
                              } else {
                                setFormData({ ...formData, services: formData.services.filter(s => s !== service.value) });
                              }
                            }}
                          />
                          <span>{service.emoji}</span>
                          <span className="text-sm">{service.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Région *</Label>
                      <Select value={formData.region} onValueChange={(v) => setFormData({ ...formData, region: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {REGIONS.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Ville *</Label>
                      <Input
                        placeholder="Ex: La Marsa"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Prix de base par personne (TND) *</Label>
                    <Input
                      type="number"
                      min={20}
                      max={500}
                      value={formData.base_price_per_person}
                      onChange={(e) => setFormData({ ...formData, base_price_per_person: parseInt(e.target.value) })}
                    />
                    <p className="text-xs text-gray-500 mt-1">Commission plateforme: 15%</p>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Specialties & Dishes */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div>
                    <Label>Spécialités</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="Ex: Cuisine traditionnelle"
                        value={newSpecialty}
                        onChange={(e) => setNewSpecialty(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSpecialty())}
                      />
                      <Button type="button" variant="outline" onClick={handleAddSpecialty}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.specialties.map((spec, i) => (
                        <Badge key={i} variant="secondary" className="pr-1">
                          {spec}
                          <button
                            onClick={() => setFormData({
                              ...formData,
                              specialties: formData.specialties.filter((_, idx) => idx !== i)
                            })}
                            className="ml-1 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Plats signatures</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="Ex: Couscous au poisson"
                        value={newDish}
                        onChange={(e) => setNewDish(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDish())}
                      />
                      <Button type="button" variant="outline" onClick={handleAddDish}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.signature_dishes.map((dish, i) => (
                        <Badge key={i} className="bg-[#E07A5F]/10 text-[#E07A5F] pr-1">
                          {dish}
                          <button
                            onClick={() => setFormData({
                              ...formData,
                              signature_dishes: formData.signature_dishes.filter((_, idx) => idx !== i)
                            })}
                            className="ml-1 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Langues parlées</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['Français', 'Arabe', 'Anglais', 'Italien', 'Allemand'].map((lang) => (
                        <label
                          key={lang}
                          className={`px-3 py-1 rounded-full border cursor-pointer text-sm transition-all ${
                            formData.languages.includes(lang)
                              ? 'border-[#1E4ED8] bg-[#1E4ED8] text-white'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={formData.languages.includes(lang)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, languages: [...formData.languages, lang] });
                              } else {
                                setFormData({ ...formData, languages: formData.languages.filter(l => l !== lang) });
                              }
                            }}
                          />
                          {lang}
                        </label>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <DialogFooter className="flex justify-between mt-6">
              {step > 1 ? (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  Retour
                </Button>
              ) : (
                <div />
              )}
              {step < 3 ? (
                <Button onClick={() => setStep(step + 1)} className="bg-[#1E4ED8]">
                  Suivant
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} className="bg-[#E07A5F]">
                  {submitting ? 'Création...' : 'Créer mon profil'}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Booking Dialog
const BookingDialog = ({ open, chef, onClose, user, api }) => {
  const navigate = useNavigate();
  const [bookingForm, setBookingForm] = useState({
    service_type: '',
    event_date: '',
    event_time: '19:00',
    guest_count: 4,
    address: '',
    city: '',
    menu_notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Connecte-toi pour réserver');
      navigate('/auth');
      return;
    }

    if (!bookingForm.service_type || !bookingForm.event_date || !bookingForm.address) {
      toast.error('Remplis tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/cultural/bookings', {
        chef_profile_id: chef.id,
        service_type: bookingForm.service_type,
        event_date: bookingForm.event_date,
        event_time: bookingForm.event_time,
        duration_hours: 4,
        guest_count: bookingForm.guest_count,
        location: {
          address: bookingForm.address,
          city: bookingForm.city || chef.city
        },
        menu_notes: bookingForm.menu_notes
      });
      toast.success('Demande de réservation envoyée!');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setSubmitting(false);
    }
  };

  if (!chef) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-fraunces text-xl">
            Réserver {chef.user?.display_name || chef.user?.username}
          </DialogTitle>
          <DialogDescription>
            Demande une réservation pour ton événement
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Type de service *</Label>
            <Select value={bookingForm.service_type} onValueChange={(v) => setBookingForm({ ...bookingForm, service_type: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un service" />
              </SelectTrigger>
              <SelectContent>
                {chef.services?.map((service) => {
                  const info = SERVICE_TYPES.find(s => s.value === service);
                  return (
                    <SelectItem key={service} value={service}>
                      {info?.emoji} {info?.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={bookingForm.event_date}
                onChange={(e) => setBookingForm({ ...bookingForm, event_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label>Heure</Label>
              <Input
                type="time"
                value={bookingForm.event_time}
                onChange={(e) => setBookingForm({ ...bookingForm, event_time: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label>Nombre de convives</Label>
            <Input
              type="number"
              min={2}
              max={50}
              value={bookingForm.guest_count}
              onChange={(e) => setBookingForm({ ...bookingForm, guest_count: parseInt(e.target.value) })}
            />
          </div>

          <div>
            <Label>Adresse de l'événement *</Label>
            <Input
              placeholder="Ex: 123 Avenue Habib Bourguiba"
              value={bookingForm.address}
              onChange={(e) => setBookingForm({ ...bookingForm, address: e.target.value })}
            />
          </div>

          <div>
            <Label>Notes sur le menu</Label>
            <Textarea
              placeholder="Allergies, préférences, thème particulier..."
              value={bookingForm.menu_notes}
              onChange={(e) => setBookingForm({ ...bookingForm, menu_notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Estimation (acompte 30%)</span>
              <span className="font-bold text-lg">
                ~{Math.round(chef.base_price_per_person * bookingForm.guest_count * 0.3)} TND
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Le prix final sera confirmé par le chef</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-[#E07A5F]">
            {submitting ? 'Envoi...' : 'Envoyer la demande'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function PrivateChefPage() {
  const { t, i18n } = useTranslation();
  const { user, api: authApi } = useAuth();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const getServiceLabel = (service) => lang === 'en' ? service.labelEn : service.labelFr;
  const navigate = useNavigate();
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    region: 'all',
    service_type: 'all',
    min_rating: 0,
  });
  const [showBecomeChef, setShowBecomeChef] = useState(false);
  const [bookingDialog, setBookingDialog] = useState({ open: false, chef: null });
  const [userIsChef, setUserIsChef] = useState(false);

  useEffect(() => {
    fetchChefs();
    checkUserChefStatus();
  }, [filters]);

  const fetchChefs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.region && filters.region !== 'all') params.append('region', filters.region);
      if (filters.service_type && filters.service_type !== 'all') params.append('service_type', filters.service_type);
      if (filters.min_rating > 0) params.append('min_rating', filters.min_rating);
      
      const response = await api.get(`/cultural/chefs?${params}`);
      setChefs(response.data.chefs || []);
    } catch (error) {
      console.error('Error fetching chefs:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkUserChefStatus = async () => {
    if (!user) return;
    try {
      const response = await api.get('/cultural/my-chef-profile');
      setUserIsChef(!!response.data);
    } catch {
      setUserIsChef(false);
    }
  };

  const handleCreateChefProfile = async (formData) => {
    await api.post('/cultural/chef-profiles', formData);
    setUserIsChef(true);
    fetchChefs();
  };

  const userBarka = user?.barka_points || user?.barka || 0;

  return (
    <div className="min-h-screen bg-[#F8F5F2]" data-testid="private-chef-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E4ED8] via-[#3B82F6] to-[#1E4ED8]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 border-2 border-white/30 rounded-full" />
          <div className="absolute bottom-10 left-10 w-32 h-32 border-2 border-white/30 rounded-full" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-12">
          <Badge className="bg-white/20 text-white mb-4">
            <ChefHat className="w-3 h-3 mr-1" />
            {t('privateChefs.title', 'Private Chefs')}
          </Badge>
          
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <h1 className="font-fraunces text-4xl md:text-5xl font-bold text-white mb-3">
                {t('privateChefs.title', 'Private Chefs')}
              </h1>
              <p className="text-white/70 text-lg max-w-xl">
                {t('privateChefs.subtitle', 'Book an authentic Tunisian culinary experience')}
              </p>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {user && !userIsChef && (
                <Button
                  size="lg"
                  className="bg-[#E07A5F] hover:bg-[#D9381E] rounded-full px-8"
                  onClick={() => setShowBecomeChef(true)}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  {t('privateChefs.becomeChef', 'Become a Private Chef')}
                  {userBarka >= REQUIRED_BARKA ? (
                    <CheckCircle className="w-4 h-4 ml-2 text-green-300" />
                  ) : (
                    <Lock className="w-4 h-4 ml-2 text-white/50" />
                  )}
                </Button>
              )}
              {userIsChef && (
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full"
                  onClick={() => navigate('/chef-dashboard')}
                >
                  <Briefcase className="w-5 h-5 mr-2" />
                  {t('privateChefs.myDashboard', 'My Chef Dashboard')}
                </Button>
              )}
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex flex-wrap gap-8 mt-8">
            <div className="text-white/80">
              <span className="text-3xl font-bold text-white">{chefs.length}</span>
              <span className="ml-2">{t('privateChefs.chefsAvailable', 'Chefs available')}</span>
            </div>
            <div className="text-white/80">
              <span className="text-3xl font-bold text-white">{REGIONS.length}</span>
              <span className="ml-2">{t('privateChefs.regionsCovered', 'Regions covered')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Service Type Buttons */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
            <Button
              variant={filters.service_type === 'all' ? 'default' : 'ghost'}
              className={`rounded-full ${filters.service_type === 'all' ? 'bg-[#1E4ED8] text-white' : ''}`}
              onClick={() => setFilters({ ...filters, service_type: 'all' })}
            >
              {t('privateChefs.allServices', 'All Services')}
            </Button>
            {SERVICE_TYPES.map((service) => (
              <Button
                key={service.value}
                variant={filters.service_type === service.value ? 'default' : 'ghost'}
                className={`rounded-full whitespace-nowrap ${
                  filters.service_type === service.value ? 'bg-[#1E4ED8] text-white' : ''
                }`}
                onClick={() => setFilters({ ...filters, service_type: service.value })}
              >
                <span className="mr-2">{service.emoji}</span>
                {service.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters & Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Select value={filters.region} onValueChange={(v) => setFilters({ ...filters, region: v })}>
              <SelectTrigger className="w-[180px] rounded-full bg-white">
                <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Toutes les régions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les régions</SelectItem>
                {REGIONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.min_rating.toString()} 
              onValueChange={(v) => setFilters({ ...filters, min_rating: parseFloat(v) })}
            >
              <SelectTrigger className="w-[150px] rounded-full bg-white">
                <Star className="w-4 h-4 mr-2 text-[#FFD700]" />
                <SelectValue placeholder="Note min." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Toutes notes</SelectItem>
                <SelectItem value="4">4+ étoiles</SelectItem>
                <SelectItem value="4.5">4.5+ étoiles</SelectItem>
                <SelectItem value="4.8">4.8+ étoiles</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Badge variant="outline" className="text-gray-500">
            {chefs.length} chefs trouvés
          </Badge>
        </div>

        {/* Chefs Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="overflow-hidden rounded-2xl">
                <Skeleton className="h-32" />
                <div className="p-6 pt-16 space-y-3">
                  <Skeleton className="h-6 w-2/3 mx-auto" />
                  <Skeleton className="h-4 w-1/2 mx-auto" />
                  <Skeleton className="h-10 w-full mt-4" />
                </div>
              </Card>
            ))}
          </div>
        ) : chefs.length === 0 ? (
          <Card className="p-12 text-center rounded-2xl">
            <div className="w-20 h-20 rounded-full bg-[#1E4ED8]/10 flex items-center justify-center mx-auto mb-4">
              <ChefHat className="w-10 h-10 text-[#1E4ED8]" />
            </div>
            <h3 className="font-fraunces text-xl font-bold mb-2">Aucun chef disponible</h3>
            <p className="text-gray-500 mb-6">
              Modifie tes critères de recherche ou sois le premier chef de ta région!
            </p>
            {user && !userIsChef && (
              <Button className="bg-[#E07A5F]" onClick={() => setShowBecomeChef(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Devenir Chef Privé
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chefs.map((chef) => (
              <ChefCard
                key={chef.id}
                chef={chef}
                onBook={(c) => setBookingDialog({ open: true, chef: c })}
              />
            ))}
          </div>
        )}

        {/* Become Chef Info */}
        {user && !userIsChef && (
          <Card className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6] text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <ChefHat className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="font-fraunces text-2xl font-bold">Deviens Chef Privé!</h3>
                  <p className="text-white/80">
                    {userBarka >= REQUIRED_BARKA 
                      ? "Tu as assez de BARKA! Crée ton profil maintenant."
                      : `Encore ${REQUIRED_BARKA - userBarka} BARKA pour débloquer cette fonctionnalité.`
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <Flame className="w-6 h-6 mx-auto mb-1 text-[#E07A5F]" />
                  <p className="font-bold text-xl">{userBarka}</p>
                  <p className="text-xs text-white/60">BARKA</p>
                </div>
                <Button
                  size="lg"
                  className="bg-white text-[#1E4ED8] hover:bg-white/90 rounded-full"
                  onClick={() => setShowBecomeChef(true)}
                  disabled={userBarka < REQUIRED_BARKA}
                >
                  {userBarka >= REQUIRED_BARKA ? 'Créer mon profil' : 'Bientôt disponible'}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Dialogs */}
      <BecomeChefDialog
        open={showBecomeChef}
        onClose={() => setShowBecomeChef(false)}
        user={user}
        onSubmit={handleCreateChefProfile}
      />
      
      <BookingDialog
        open={bookingDialog.open}
        chef={bookingDialog.chef}
        onClose={() => setBookingDialog({ open: false, chef: null })}
        user={user}
        api={api}
      />
    </div>
  );
}
